import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, BookOpen } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations';
import { getMyRole, setRole } from '../../services/studioApi';
import { authService } from '../../services/authService';
import './Studio.css';

export default function StudioLanding() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (translations[language]?.studio) || {};
  const [loading, setLoading] = useState(true);
  const [setting, setSetting] = useState(false);

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/signin');
      return;
    }
    getMyRole()
      .then(({ role }) => {
        if (role === 'teacher') navigate('/studio/teacher', { replace: true });
        else if (role === 'student') navigate('/studio/student', { replace: true });
        else setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [navigate]);

  const chooseRole = async (role) => {
    setSetting(true);
    try {
      await setRole(role);
      // Update stored user
      const user = authService.getUser();
      if (user) authService.saveAuth(authService.getToken(), { ...user, role });
      navigate(role === 'teacher' ? '/studio/teacher' : '/studio/student', { replace: true });
    } catch (e) {
      setSetting(false);
    }
  };

  if (loading || setting) {
    return (
      <div className="studio-page" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <div className="loading-center"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="studio-page">
      <div className="studio-main" style={{ maxWidth: 700, textAlign: 'center', paddingTop: '4rem' }}>
        <div style={{ marginBottom: '0.75rem' }}>
          <span style={{
            background: 'linear-gradient(135deg,#667eea,#764ba2)',
            color: 'white', borderRadius: 20, padding: '0.3rem 1rem',
            fontSize: '0.8rem', fontWeight: 700, letterSpacing: 1
          }}>ANIMLEARN STUDIO</span>
        </div>
        <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#1a1a2e', margin: '0.75rem 0' }}>
          {t.landing?.title || 'Welcome to AnimLearn Studio'}
        </h1>
        <p style={{ color: '#666', fontSize: '1rem', marginBottom: '2.5rem' }}>
          {t.landing?.subtitle || 'Choose your role to get started'}
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', maxWidth: 520, margin: '0 auto' }}>
          <button
            className="studio-card"
            style={{ cursor: 'pointer', border: '2px solid transparent', textAlign: 'left', transition: 'all 0.2s' }}
            onClick={() => chooseRole('teacher')}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#667eea'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}
          >
            <div style={{
              width: 52, height: 52, borderRadius: 14,
              background: 'linear-gradient(135deg,#667eea,#764ba2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: '1rem'
            }}>
              <GraduationCap size={26} color="white" />
            </div>
            <h3 style={{ margin: '0 0 0.4rem', fontSize: '1rem', fontWeight: 700, color: '#1a1a2e' }}>
              {t.landing?.teacher || 'Teacher'}
            </h3>
            <p style={{ margin: 0, fontSize: '0.82rem', color: '#888', lineHeight: 1.5 }}>
              {t.landing?.teacherDesc || 'Upload syllabi, create courses, manage assignments and track student progress'}
            </p>
          </button>

          <button
            className="studio-card"
            style={{ cursor: 'pointer', border: '2px solid transparent', textAlign: 'left', transition: 'all 0.2s' }}
            onClick={() => chooseRole('student')}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#4caf50'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}
          >
            <div style={{
              width: 52, height: 52, borderRadius: 14,
              background: 'linear-gradient(135deg,#4caf50,#66bb6a)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: '1rem'
            }}>
              <BookOpen size={26} color="white" />
            </div>
            <h3 style={{ margin: '0 0 0.4rem', fontSize: '1rem', fontWeight: 700, color: '#1a1a2e' }}>
              {t.landing?.student || 'Student'}
            </h3>
            <p style={{ margin: 0, fontSize: '0.82rem', color: '#888', lineHeight: 1.5 }}>
              {t.landing?.studentDesc || 'Join courses with an invite code, watch videos, and practice with adaptive exercises'}
            </p>
          </button>
        </div>

        <p style={{ marginTop: '2rem', color: '#aaa', fontSize: '0.8rem' }}>
          {t.landing?.roleNote || 'You can update your role later from settings'}
        </p>
      </div>
    </div>
  );
}
